import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import { Button, Card, Col, Flex, Form, Input, Row } from 'antd'
import { FormInstance } from 'antd/lib'
import { useEffect, useState } from 'react'

import { usePersonsServiceGetPersonByIdEndpoint } from 'src/api/queries'
import {
  ClassifierCategory,
  ClassifierType,
  Permissions
} from 'src/api/requests'
import { requiredRules } from 'src/app-constants'
import {
  InternalInstitutionsSelect,
  ClassifierInfiniteSelect,
  ReferralDoctorFields
} from 'src/components'
import { UsersInfinitySelect } from 'src/features/activity/components'
import { SearchPersonModal } from 'src/features/persons/components'
import { getFullName } from 'src/features/users/utils'
import { useClassifierId, useUser } from 'src/hooks'
import { useLocales } from 'src/locales'

type Props = {
  form: FormInstance
}

export const ManuallyAddInvestigation = ({ form }: Props) => {
  const { t } = useLocales()

  const [isOpenModal, setIsOpenModal] = useState(false)
  const [personId, setPersonId] = useState<string>()

  const { hasPermission } = useUser()

  const hasUsersAccess = hasPermission([
    Permissions.IDENTITY_USERS_VIEW_ALL,
    Permissions.IDENTITY_USERS_VIEW_INSTITUTION
  ])

  const { data: person } = usePersonsServiceGetPersonByIdEndpoint(
    { id: String(personId) },
    [personId],
    { enabled: !!personId }
  )

  const onToggle = () => setIsOpenModal((prev) => !prev)

  const institution = Form.useWatch('institutionId', form)
  const classifierId = useClassifierId(
    ClassifierType.TEST_TYPE,
    ClassifierCategory.INVESTIGATIONS
  )
  const specimenTypeId = useClassifierId(
    ClassifierType.SPECIMEN_TYPE,
    ClassifierCategory.INVESTIGATIONS
  )

  const onInstitutionChange = (val: string) => {
    form.setFieldValue('institutionId', val)
    form.resetFields(['doctorId'])
  }

  const onAddClick = (val: string) => {
    setPersonId(val)
    onToggle()
  }

  useEffect(() => {
    if (person && personId) {
      form.setFieldsValue({
        simeCode: person.simeCode,
        tarvCode: person.tarvCode,
        personId: person.id,
        personName: getFullName(person.firstName, person.lastName)
      })
    }
  }, [person, personId])

  return (
    <Row gutter={[24, 24]}>
      <Col span={12}>
        <Form.Item
          name="institutionId"
          label={t('common.medical_institution')}
          rules={requiredRules}
        >
          <InternalInstitutionsSelect
            onChange={onInstitutionChange}
            placeholder={t('common.medical_institution')}
          />
        </Form.Item>
      </Col>

      {hasUsersAccess && (
        <Col span={12}>
          <Form.Item
            name="doctorId"
            label={t('analysis.medic_modal_title')}
            rules={requiredRules}
          >
            <UsersInfinitySelect
              disabled={!institution}
              legalEntity={institution}
              placeholder={t('analysis.medic_modal_title')}
            />
          </Form.Item>
        </Col>
      )}
      <Col span={12}>
        <Form.Item
          name="testTypeId"
          label={t('analysis.type')}
          rules={requiredRules}
        >
          <ClassifierInfiniteSelect
            id={classifierId}
            placeholder={t('analysis.type')}
          />
        </Form.Item>
      </Col>

      <Col span={12}>
        <Form.Item
          name="specimenType"
          label={t('consultations.specimen_type')}
          rules={requiredRules}
        >
          <ClassifierInfiniteSelect
            id={specimenTypeId}
            placeholder={t('consultations.specimen_type')}
          />
        </Form.Item>
      </Col>

      <ReferralDoctorFields form={form} />

      <Col span={24}>
        <Card>
          {personId ? (
            <Row gutter={[24, 24]}>
              <Col span={7}>
                <Form.Item
                  name="personName"
                  label={`${t('form.last_name')} ${t('form.first_name')}`}
                  rules={requiredRules}
                >
                  <Input disabled placeholder={t('common.name')} />
                </Form.Item>
              </Col>
              <Col span={7}>
                <Form.Item
                  name="simeCode"
                  label={t('common.sime_code')}
                  rules={requiredRules}
                >
                  <Input disabled placeholder={t('common.sime_code')} />
                </Form.Item>
              </Col>

              <Col span={7}>
                <Form.Item name="tarvCode" label={t('common.tarv_code')}>
                  <Input disabled placeholder={t('common.tarv_code')} />
                </Form.Item>
              </Col>

              <Col span={1}>
                <Form.Item label={<></>}>
                  <Button
                    danger
                    onClick={() => setPersonId(undefined)}
                    icon={<DeleteOutlined />}
                  />
                </Form.Item>
              </Col>
            </Row>
          ) : (
            <Flex justify="center" align="center">
              <Button icon={<PlusOutlined />} onClick={onToggle}>
                {t('investigations.add_person')}
              </Button>
            </Flex>
          )}
        </Card>
      </Col>

      {isOpenModal && !personId && (
        <SearchPersonModal onAddClick={onAddClick} onCancel={onToggle} />
      )}
    </Row>
  )
}
